import { type RumEvent, datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

// /join and Connect are both served from connect.treatwell.* . The RUM session
// cookie (`_dd_s`) is scoped to that host, and Datadog keeps one session per
// RUM application. These must be Connect's application id and client token
// (connect-desktop `server/env.js`) so a partner who finishes sign-up and
// continues into Connect stays in the same replay. `service` stays
// `self-signup`, which is what keeps these views filterable inside that
// application.
const DATADOG_APPLICATION_ID = process.env.DATADOG_APPLICATION_ID;
const DATADOG_CLIENT_TOKEN = process.env.DATADOG_CLIENT_TOKEN;
const TW_ENV_NAME = process.env.TW_ENV_NAME;
const APPLICATION_VERSION = process.env.APPLICATION_VERSION;

// Errors originating from injected / third-party code are noise, not ours.
// RUM only exposes the stacks as strings, so we substring-match them — this also
// catches markers in deeper frames (e.g. a chrome-extension wrapping fetch).
// Handled errors (console.error, non-Error throws) only carry the caller in
// handling_stack; their stack is a placeholder.
const INJECTED_CODE_MARKERS = [
  'chrome-extension://',
  'gtm.js', // Google Tag Manager
  'scripttemplates/otSDKStub', // OneTrust SDK
  'tr.capterra.com', // Capterra pixel, injected via GTM
  'callpage.io', // CallPage widget, injected via GTM
];

// Messages that carry nothing actionable. "Script error." is what browsers
// report for errors thrown by cross-origin scripts; our bundle is same-origin,
// so it is always third-party.
const IGNORED_MESSAGES = [
  'Uncaught "Script error."',
  'Uncaught "ResizeObserver loop completed with undelivered notifications."',
];

function isInjectedError(event: RumEvent): boolean {
  if (event.type !== 'error') return false;
  const { message, stack, handling_stack } = event.error;
  if (IGNORED_MESSAGES.includes(message)) return true;
  return INJECTED_CODE_MARKERS.some(
    marker => stack?.includes(marker) || handling_stack?.includes(marker),
  );
}

export const initDatadogRum = () => {
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.eu',
    service: 'self-signup',
    env: TW_ENV_NAME || 'local',

    // Specify a version number to identify the deployed version of your application in Datadog
    version: APPLICATION_VERSION || '0.0.1',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin({ router: false })],
    beforeSend: event => !isInjectedError(event),
  });
};
